<template>
  <div id="mobile-app">
    <router-view v-slot="{ Component, route }">
      <transition name="page-fade" mode="out-in">
        <component :is="Component" :key="route.path" />
      </transition>
    </router-view>

    <!-- 底部导航栏（登录页不显示） -->
    <nav v-if="showTabBar" class="tab-bar">
      <router-link to="/overview" class="tab-item" active-class="tab-active">
        <el-icon :size="22"><DataBoard /></el-icon>
        <span>总览</span>
      </router-link>
      <router-link to="/records-inspect" class="tab-item" active-class="tab-active">
        <el-icon :size="22"><Checked /></el-icon>
        <span>巡检记录</span>
      </router-link>
      <router-link to="/hazards" class="tab-item" active-class="tab-active">
        <el-icon :size="22"><Warning /></el-icon>
        <span>隐患</span>
      </router-link>
      <router-link to="/me" class="tab-item" active-class="tab-active">
        <el-icon :size="22"><UserFilled /></el-icon>
        <span>我的</span>
      </router-link>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const showTabBar = computed(() => !route.path.startsWith('/login'))
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  background-color: #f5f7fa;
  -webkit-font-smoothing: antialiased;
}

#mobile-app {
  min-height: 100vh;
  padding-bottom: 62px;
}

/* 页面切换动画 */
.page-fade-enter-active, .page-fade-leave-active { transition: opacity 0.18s ease; }
.page-fade-enter-from, .page-fade-leave-to { opacity: 0; }

/* 底部导航 */
.tab-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  height: 58px;
  background: #fff;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-top: 1px solid #e4e7ed;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.05);
  z-index: 100;
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  text-decoration: none;
  color: #909399;
  font-size: 11px;
  flex: 1;
  height: 100%;
  transition: color 0.2s;
}

.tab-active { color: #1a73e8; }

@media (max-width: 768px) {
  .el-dialog { width: 92% !important; border-radius: 14px !important; }
  .el-drawer { border-radius: 16px 16px 0 0 !important; }
}

/* ===== 通用移动端页面构件 ===== */
.mobile-page { min-height: calc(100vh - 58px); padding-bottom: 20px; }

.mobile-header {
  background: linear-gradient(135deg, #1a73e8 0%, #4285f4 60%, #5b8def 100%);
  color: #fff;
  padding: 18px 16px 20px;
}

.mobile-header h2 {
  font-size: 19px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-header p { font-size: 12.5px; opacity: 0.9; margin-top: 4px; }

.mobile-body { padding: 12px 12px 8px; }

/* 子页顶部（带返回） */
.sub-nav {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px;
  background: linear-gradient(135deg, #1a73e8 0%, #4285f4 60%, #5b8def 100%);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.sub-nav .back-btn {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border: none; border-radius: 10px;
  background: rgba(255,255,255,0.18); color: #fff;
  cursor: pointer;
}

.sub-nav .nav-title { color: #fff; font-size: 16px; font-weight: 700; }

.sub-nav .nav-right { width: 34px; display: flex; justify-content: flex-end; }

.sub-nav .nav-right .refresh-btn {
  width: 34px; height: 34px; border: none; border-radius: 10px;
  background: rgba(255,255,255,0.18); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}

/* 统计卡 */
.stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.stat-card {
  background: #fff; border-radius: 14px; padding: 14px 16px;
  box-shadow: 0 3px 12px rgba(31,45,61,0.07);
  cursor: pointer; transition: transform .15s;
}
.stat-card:active { transform: scale(.97); }
.stat-num { font-size: 28px; font-weight: 800; line-height: 1.1; }
.stat-label { font-size: 12.5px; color: #909399; margin-top: 3px; }

/* 白卡 */
.white-card {
  background: #fff; border-radius: 14px; padding: 14px;
  box-shadow: 0 3px 12px rgba(31,45,61,0.06); margin-bottom: 10px;
}

.section-title {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 15px; font-weight: 700; color: #303133;
  margin: 16px 2px 10px;
}

.section-title .link { font-size: 12.5px; font-weight: 400; color: #1a73e8; cursor: pointer; }

/* 状态胶囊 */
.pill { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; flex-shrink: 0; }
.pill-blue { background: #e8f0fe; color: #1a73e8; }
.pill-green { background: #e6f4ea; color: #188038; }
.pill-orange { background: #fef7e0; color: #b06000; }
.pill-red { background: #fce8e6; color: #c5221f; }
.pill-gray { background: #f1f3f4; color: #5f6368; }
.pill-purple { background: #f3e8fd; color: #8430ce; }

/* 空态/加载 */
.center-box { text-align: center; padding: 46px 0; color: #909399; }
.center-box .sub-text { font-size: 13px; margin-top: 10px; }

.chip-row { display: flex; gap: 6px; overflow-x: auto; padding: 2px; -webkit-overflow-scrolling: touch; margin-bottom: 10px; }
.chip {
  flex-shrink: 0; padding: 5px 13px; background: #fff; border-radius: 16px;
  font-size: 12.5px; color: #5f6368; border: 1px solid #e4e7ed; cursor: pointer;
  white-space: nowrap; user-select: none;
}
.chip.on { background: #1a73e8; color: #fff; border-color: #1a73e8; font-weight: 600; }

.mono { font-family: 'Courier New', monospace; letter-spacing: .5px; }

/* 全屏查看器 */
.viewer {
  position: fixed; inset: 0; background: rgba(0,0,0,.94); z-index: 300;
  display: flex; flex-direction: column;
}
.viewer-top { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px 4px; color: #fff; }
.viewer-top button {
  width: 38px; height: 38px; display: flex; align-items: center; justify-content: center;
  border: none; border-radius: 50%; color: #fff; background: rgba(255,255,255,.15); cursor: pointer;
}
.viewer-count { font-size: 13px; }
.viewer-body { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.viewer-body img { max-width: 100%; max-height: 100%; object-fit: contain; }
.viewer-body video { max-width: 100%; max-height: 100%; }
.viewer-btns { display: flex; justify-content: center; gap: 40px; padding: 12px 0 22px; }
.viewer-btns button {
  width: 50px; height: 50px; border-radius: 50%; border: none; color: #fff;
  background: rgba(255,255,255,.18); font-size: 20px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.viewer-btns button:disabled { opacity: .3; }
.viewer-enter-active, .viewer-leave-active { transition: opacity .22s ease; }
.viewer-enter-from, .viewer-leave-to { opacity: 0; }
</style>
